import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import { useChatConversationQuery } from "web_myplann/Api/Chat/useChatConversationQuery";
import { useChatMessagesQuery } from "web_myplann/Api/Chat/useChatMessagesQuery";
import { useCreateChatConversationMutation } from "web_myplann/Api/Chat/useCreateChatConversationMutation";
import { useLeaveChatGroupMutation } from "web_myplann/Api/Chat/useLeaveChatGroupMutation";
import { useSendChatMessageMutation } from "web_myplann/Api/Chat/useSendChatMessageMutation";
import { dropOptimisticChatMessages } from "web_myplann/Chat/Helpers/Messages/chatMessagesCache";
import { getQueuedChatClientIds } from "web_myplann/Chat/Helpers/Messages/getQueuedChatClientIds";
import { useChatAttachments } from "web_myplann/Chat/Hooks/Attachments/useChatAttachments";
import { useChatRecipient } from "web_myplann/Chat/Hooks/Conversation/useChatRecipient";
import { useMarkChatMessagesRead } from "web_myplann/Chat/Hooks/Conversation/useMarkChatMessagesRead";
import type { ChatPageState } from "web_myplann/Chat/Types/chatPageState";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { createId } from "web_myplann/Shared/Utils/createId";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";

type Args = {
	onMessageSent: () => void;
};

export const useChatPage = ({ onMessageSent }: Args) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { state } = useLocation() as { state: ChatPageState | null };
	const queryClient = useQueryClient();
	const { t } = useResource();
	const routeParams = useParams<{
		conversationId: string;
	}>();

	const [conversationId, setConversationId] = useState(
		getTypedParam(routeParams, "conversationId", Number) ?? null,
	);
	const conversationIdRef = useRef(conversationId);

	const cleanedRef = useRef(false);
	if (!cleanedRef.current) {
		cleanedRef.current = true;
		dropOptimisticChatMessages(
			queryClient,
			conversationId,
			getQueuedChatClientIds(queryClient, conversationId),
		);
	}

	const advisorIdParam = getTypedParam(searchParams, "advisorId", String);
	const {
		advisorId,
		advisorName,
		advisorPhoto,
		title,
		titlePhoto,
		isLoading: isRecipientLoading,
	} = useChatRecipient(conversationId, advisorIdParam);

	const { data: conversation } = useChatConversationQuery(conversationId);

	const [input, setInput] = useState(state?.draftMessage ?? "");

	const {
		data: messages = [],
		isLoading,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useChatMessagesQuery(conversationId);
	useMarkChatMessagesRead(conversationId, messages);

	const { mutate: createConversation } = useCreateChatConversationMutation();
	const { mutate: sendChatMessage } =
		useSendChatMessageMutation(conversationId);
	const { mutate: leaveGroup, isPending: isLeavingConversation } =
		useLeaveChatGroupMutation();

	const takeCaption = () => {
		const caption = input.trim();
		setInput("");

		return caption;
	};

	const showSendError = () => toast.error(t(Resources.Chat.sendError));

	const sendText = (content: string, clientId: string) => {
		const targetConversationId = conversationIdRef.current;

		if (targetConversationId === null) {
			createConversation(
				{ advisorId, content, clientId },
				{
					onSuccess: ({ conversationId: newId }) => {
						if (conversationIdRef.current !== null) return;

						conversationIdRef.current = newId;
						setConversationId(newId);
						navigate(Routing.Chat.Detail.path(String(newId), advisorId), {
							replace: true,
						});
					},
					onError: showSendError,
				},
			);
			return;
		}

		sendChatMessage(
			{ conversationId: targetConversationId, content, clientId },
			{ onError: showSendError },
		);
	};

	const sendAttachment = (
		file: File,
		caption: string,
		clientId: string,
		{ onSettled }: { onSettled: () => void },
	) => {
		const targetConversationId = conversationIdRef.current;
		if (targetConversationId === null) return;

		sendChatMessage(
			{
				conversationId: targetConversationId,
				content: caption,
				clientId,
				file,
			},
			{ onSuccess: onSettled, onError: showSendError },
		);
	};

	const { onRetry, onPickPhoto, onPickGallery, onPickFile } =
		useChatAttachments({
			conversationIdRef,
			takeCaption,
			sendText,
			sendAttachment,
			onMessageSent,
		});

	const handleSend = () => {
		if (!input.trim()) return;

		sendText(takeCaption(), createId());
		onMessageSent();
	};

	const handleLeaveConversation = () => {
		if (conversationId === null || isLeavingConversation) return;

		leaveGroup(conversationId, {
			onSuccess: () => navigate(Routing.Chat.List.path(), { replace: true }),
		});
	};

	return {
		title,
		titlePhoto,
		advisorName,
		advisorPhoto,
		canLeaveConversation: conversation?.isGroup ?? false,
		isLeavingConversation,
		messages,
		isLoading: isLoading || isRecipientLoading,
		hasOlder: hasNextPage,
		isLoadingOlder: isFetchingNextPage,
		loadOlder: () => fetchNextPage(),
		input,
		setInput,
		canAttach: conversationId !== null,
		onSend: handleSend,
		onRetry,
		onPickPhoto,
		onPickGallery,
		onPickFile,
		onLeaveConversation: handleLeaveConversation,
		onBack: () => navigate(-1),
	};
};
